import { useRef, type ReactNode } from "react";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Eye,
  Layers,
  PlayCircle,
  RefreshCw,
  Gauge,
  ShieldCheck,
  Copy,
  Terminal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  getAgentPromptExplanation,
  getHostLabel,
  getLaunchCommand,
  buildOrchestratorCommand,
  buildOrchestratorFeatureCommand,
} from "@/lib/chat/cli-guidance";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is the Orchestrator?                                        */
/* -------------------------------------------------------------------------- */

export function WhatIsOrchestratorStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Meet the Orchestrator">
        <p className="text-sm leading-7 text-muted-foreground">
          The Orchestrator is like a team lead that reads your plan and delegates the work.
          It spawns <strong className="text-foreground/90">sub-agents</strong> for each feature —
          running them <strong className="text-foreground/90">in parallel</strong> when possible, or sequentially when one depends on another.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          You {"don't"} need to tell it what to do step by step — it already knows because the Planner
          set everything up.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="What does it actually do?">
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={Layers}
            title="Reads your plan"
            description="It loads the epic the Planner created and understands the full scope of work."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={PlayCircle}
            title="Delegates to sub-agents"
            description="Spawns feature-workers that implement code in parallel, respecting dependencies between features."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={ShieldCheck}
            title="Reviews the results"
            description="After each phase, a reviewer agent verifies the work. Only marks things done when they actually pass."
            accentClass="text-amber-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What do you need to do?">
        <p className="text-sm leading-7 text-muted-foreground">
          Honestly? Not much. You tell it which epic to run, and it handles the rest. You can
          watch it work in your terminal, or check progress in SpecTree at any time. {"It's"} like
          handing a project to a team of developers and watching the progress updates roll in.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bot className="h-4 w-4 text-emerald-400" />
            <span>You say: run this epic</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 text-amber-400" />
            <span>Orchestrator works</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span>Features get done</span>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Run the Orchestrator                                             */
/* -------------------------------------------------------------------------- */

export function RunOrchestratorStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title={`Open your ${getHostLabel(host)}`}>
        <p className="text-sm leading-7 text-muted-foreground">
          Same as always — go to your terminal where {getHostLabel(host)} is running. If {"it's"} not open:
        </p>
        <CopyableTerminal title="Terminal" copyText={getLaunchCommand(host)}>
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">{getLaunchCommand(host)}</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Select the recommended model">
        <p className="text-sm leading-7 text-muted-foreground">
          Your epic page shows a{" "}
          <strong className="text-foreground/90">Model Recommendation</strong> based on the
          {"epic's"} complexity. Before pasting the execute command, make sure your {getHostLabel(host)}
          session is running the right model so the Orchestrator has enough horsepower.
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText="/model">
          <div className="space-y-1.5">
            <p>
              <span className="text-amber-400/70">{">"} </span>
              <span className="text-foreground">/model</span>
            </p>
            <p className="text-muted-foreground/60">↕ Select Model:</p>
            <p className="rounded bg-white/[0.06] px-2 py-0.5 text-amber-400">▸ Claude Opus 4.6</p>
            <p className="text-muted-foreground/50">{"  "}Claude Sonnet 4.6</p>
            <p className="text-muted-foreground/50">{"  "}Claude Haiku 4.5</p>
          </div>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          Type <strong className="text-foreground/90">/model</strong> in the CLI, then pick the
          model that matches {"SpecTree's"} recommendation on the epic page.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={3} title="Copy the execute command from SpecTree">
        <p className="text-sm leading-7 text-muted-foreground">
          On the same epic page, {"you'll"} see an{" "}
          <strong className="text-foreground/90">Execute this Epic</strong> section with a
          ready-to-copy command. {"Here's"} what it looks like in the actual UI:
        </p>

        {/* SpecTree UI mockup — Execute this Epic card */}
        <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.08] bg-[#0c1117]">
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Terminal className="h-4 w-4 text-foreground/70" />
              <span className="text-sm font-semibold text-foreground">Execute this Epic</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-[#060a0f] px-4 py-3">
              <code className="text-sm text-foreground">
                {buildOrchestratorCommand("ENG-E252", host)}
              </code>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/[0.08] bg-white/[0.05]">
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground/60">
              {getAgentPromptExplanation(host)}
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm leading-7 text-muted-foreground">
          Click the <strong className="text-foreground/90">copy button</strong> and paste it
          into your CLI. Want to run just one feature? Click on any feature in your plan to
          open its detail panel — {"you'll"} find an{" "}
          <strong className="text-foreground/90">Execute this Feature</strong> command there too.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={4} title="Watch it go">
        <p className="text-sm leading-7 text-muted-foreground">
          The Orchestrator starts working immediately. {"You'll"} see it announce each phase and
          feature as it goes. It might take a while depending on how big your epic is — grab a
          coffee and check back!
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — Orchestrator running`}>
          <div className="space-y-1.5">
            <p className="text-amber-400">▸ Starting orchestrator execution for epic {"\"My Epic\""}</p>
            <p className="text-muted-foreground/70">  Execution plan: 3 phases, 6 features</p>
            <p className="text-muted-foreground/60">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
            <p className="text-foreground/80">▸ Starting Phase 1 of 3...</p>
            <p className="text-foreground/80">  Working on ENG-1: Set up base config</p>
            <p className="text-emerald-400">✓ ENG-1 complete!</p>
          </div>
        </CopyableTerminal>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Monitor progress                                                 */
/* -------------------------------------------------------------------------- */

export function MonitorProgressStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Check progress in SpecTree">
        <p className="text-sm leading-7 text-muted-foreground">
          While the Orchestrator is working, you can open SpecTree and watch things move in real
          time. Features shift from <strong className="text-foreground/90">Backlog</strong> to{" "}
          <strong className="text-foreground/90">In Progress</strong> to{" "}
          <strong className="text-foreground/90">Done</strong>. {"It's"} like watching a Kanban
          board update itself.
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={Eye}
            title="Live status"
            description="Features and tasks update in real time as the Orchestrator works through them."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={CheckCircle2}
            title="Linked files"
            description="Every completed task shows which files were created or changed."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={ShieldCheck}
            title="Validations"
            description="Test results and checks stay attached so you can see what passed."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={Gauge}
            title="Session Monitor"
            description="Open the Session Monitor tab on your epic to see detailed progress, decisions, and activity in real time."
            accentClass="text-amber-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={2} title={"What if something goes wrong?"}>
        <p className="text-sm leading-7 text-muted-foreground">
          Sometimes a feature might get <strong className="text-foreground/90">blocked</strong>.
          {"That's"} okay — the Orchestrator will skip it, continue with what it can, and tell you
          what happened at the end.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          You can check blocked items anytime in SpecTree. If you need to fix something manually
          and re-run just one feature, you can do that too:
        </p>
        <CopyableTerminal
          title={`${getHostLabel(host)} — Re-run a single feature`}
          copyText={buildOrchestratorFeatureCommand("ENG-42", host)}
        >
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-foreground/70">{buildOrchestratorFeatureCommand("ENG-42", host)}</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="When it finishes">
        <p className="text-sm leading-7 text-muted-foreground">
          The Orchestrator gives you a summary at the end — how many features were completed,
          if anything was blocked, and what to do next. All the work is recorded in SpecTree
          so nothing gets lost.
        </p>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{"You're"} almost at the finish line!</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                After execution, the last step is to review the output. {"That's"} what the
                next guide covers.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
